import type { SymbolIndexWorkerOutMessage } from './protocol.js';
import { isCliImmediateShutdownRequested } from '../../../runtime/cli-process-interrupt.js';
import { CliSqliteInterrupted } from '../../../sqlite/sqlite-errors.js';
import { logImmediate } from '../../../log.js';
import { maybeLogCodeNavCacheWorkerError } from '../../../sqlite/code-nav-cache/code-nav-cache-log.js';

export type PendingRequest = {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
};

export let workerReady = false;
export let workerReadyWaiters: Array<() => void> = [];
export const pendingRequests = new Map<string, PendingRequest>();
let nextRequestId = 0;
let workerReadyListener: (() => void) | null = null;

export function createRequestId(prefix: string): string {
  nextRequestId += 1;
  return `${prefix}-${nextRequestId}-${Date.now()}`;
}

export function waitForWorkerReady(): Promise<void> {
  if (workerReady) return Promise.resolve();
  return new Promise((resolve) => {
    workerReadyWaiters.push(resolve);
  });
}

export function registerWorkerReadyListener(listener: () => void): () => void {
  workerReadyListener = listener;
  if (workerReady) listener();
  return () => {
    if (workerReadyListener === listener) workerReadyListener = null;
  };
}

export function markWorkerReady(): void {
  workerReady = true;
  for (const resolve of workerReadyWaiters) resolve();
  workerReadyWaiters = [];
  workerReadyListener?.();
}

export function handleWorkerOutMessage(message: SymbolIndexWorkerOutMessage): void {
  if (message.type === 'ready') {
    markWorkerReady();
    return;
  }
  if (message.type === 'shutdownComplete') {
    return;
  }
  if (message.type === 'cliLog') {
    logImmediate(message.line);
    return;
  }
  if (message.type === 'error') {
    const pending = message.requestId != null ? pendingRequests.get(message.requestId) : undefined;
    maybeLogCodeNavCacheWorkerError(message.message);
    if (pending != null) {
      pendingRequests.delete(message.requestId!);
      if (isCliImmediateShutdownRequested()) {
        pending.reject(new CliSqliteInterrupted());
      } else {
        pending.reject(new Error(message.message));
      }
    }
    return;
  }
  if (!('requestId' in message)) return;
  const pending = pendingRequests.get(message.requestId);
  if (pending == null) return;
  pendingRequests.delete(message.requestId);
  pending.resolve(message);
}

export function resetWorkerReadyState(): void {
  workerReady = false;
  workerReadyWaiters = [];
}

export function rejectAllPendingRequests(error: Error): void {
  for (const [, pending] of pendingRequests) {
    pending.reject(error);
  }
  pendingRequests.clear();
}